/**
 * Live Entra/Graph-format validators (Build 1 hard requirement — see 04-deferred-register 2026-06-10).
 *
 * Every profile field validates inline AS IT IS FILLED, before Save, against what Microsoft Graph
 * accepts — so nothing 500s at the Graph layer. Pure functions, ZERO dependencies, so this module
 * is safe in the Vite bundle and reusable by Build 2 (client self-service Account settings).
 *
 * KEEP IN SYNC with the server-side mirror: `api/src/lib/profile-validation.ts`
 * (the API cannot import shared-ui; the rules + limits below are duplicated there on purpose).
 *
 * Limits mirror the Microsoft Graph `user` resource constraints:
 *   displayName 256 · mobilePhone 64 · streetAddress 1024 (we cap each line at 500)
 *   city 128 · state 128 · postalCode 40 · country 128
 */

export const GRAPH_LIMITS = {
  displayName: 256,
  legalName: 256,
  phone: 64,
  addressLine: 500,
  city: 128,
  region: 128,
  postalCode: 40,
  country: 128,
} as const;

const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

/** Required. 1–256 chars, no control characters (Graph displayName). */
export function validateDisplayName(value: string): string | null {
  const s = value.trim();
  if (!s) return 'Name is required.';
  if (s.length > GRAPH_LIMITS.displayName)
    return `Name must be ${GRAPH_LIMITS.displayName} characters or fewer.`;
  if (CONTROL_CHARS.test(s)) return 'Name contains unsupported characters.';
  return null;
}

/** Optional (blank = falls back to the display name). Cosmos-only field; sane length cap. */
export function validateLegalName(value: string): string | null {
  const s = value.trim();
  if (!s) return null;
  if (s.length > GRAPH_LIMITS.legalName)
    return `Legal name must be ${GRAPH_LIMITS.legalName} characters or fewer.`;
  if (CONTROL_CHARS.test(s)) return 'Legal name contains unsupported characters.';
  return null;
}

const DOMAIN_RE =
  /^(?=.{1,253}$)[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;

/** Optional. A bare registrable hostname like `acmecorp.com` (no scheme, no path). */
export function validatePrimaryDomain(value: string): string | null {
  const s = value.trim().toLowerCase();
  if (!s) return null;
  if (/^https?:\/\//.test(s)) return 'Enter just the domain (no https://).';
  if (!DOMAIN_RE.test(s)) return 'Enter a valid domain, e.g. acmecorp.com.';
  return null;
}

const PHONE_ALLOWED = /^[+0-9 ().\-x]+$/i;

/** Optional. Graph mobilePhone: ≤64 chars; we additionally require 7–15 digits and sane characters. */
export function validatePhone(value: string): string | null {
  const s = value.trim();
  if (!s) return null;
  if (s.length > GRAPH_LIMITS.phone)
    return `Phone must be ${GRAPH_LIMITS.phone} characters or fewer.`;
  if (!PHONE_ALLOWED.test(s))
    return 'Phone may only contain digits, spaces, and + - ( ) . x';
  const digits = s.replace(/\D/g, '');
  if (digits.length < 7 || digits.length > 15)
    return 'Phone must contain 7–15 digits.';
  return null;
}

const DOB_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Optional. YYYY-MM-DD, a real calendar date, not in the future, within 120 years. */
export function validateDateOfBirth(value: string): string | null {
  const s = value.trim();
  if (!s) return null;
  if (!DOB_RE.test(s)) return 'Date of birth must be YYYY-MM-DD.';
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(5, 7));
  const d = Number(s.slice(8, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d)
    return 'That date does not exist.';
  const today = new Date();
  if (dt.getTime() > today.getTime()) return 'Date of birth cannot be in the future.';
  if (y < today.getUTCFullYear() - 120) return 'Date of birth is too far in the past.';
  return null;
}

/** The editable primary-address shape (mirrors `AddressLike` minus `type`). */
export interface AddressInput {
  line1: string;
  line2: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
}

export function isAddressBlank(a: AddressInput): boolean {
  return !a.line1.trim() && !a.line2.trim() && !a.city.trim() && !a.region.trim() &&
    !a.postalCode.trim() && !a.country.trim();
}

const POSTAL_RE = /^[a-z0-9][a-z0-9 \-]*$/i;

/**
 * Validates the address as a group. A fully blank address is valid (= no address).
 * Once any field is filled: line1, city, postal code, and country are required (the postal
 * code since 04 #170, owner-ruled s243); region optional (not every country uses one) but
 * checked when present. Returns {} when valid.
 */
export function validateAddress(a: AddressInput): Partial<Record<keyof AddressInput, string>> {
  const errors: Partial<Record<keyof AddressInput, string>> = {};
  if (isAddressBlank(a)) return errors;

  const lenCheck = (
    key: keyof AddressInput, label: string, max: number, required: boolean,
  ): void => {
    const s = a[key].trim();
    if (!s) {
      if (required) errors[key] = `${label} is required when entering an address.`;
      return;
    }
    if (s.length > max) errors[key] = `${label} must be ${max} characters or fewer.`;
    else if (CONTROL_CHARS.test(s)) errors[key] = `${label} contains unsupported characters.`;
  };

  lenCheck('line1', 'Address line 1', GRAPH_LIMITS.addressLine, true);
  lenCheck('line2', 'Address line 2', GRAPH_LIMITS.addressLine, false);
  lenCheck('city', 'City', GRAPH_LIMITS.city, true);
  lenCheck('region', 'State / region', GRAPH_LIMITS.region, false);
  lenCheck('country', 'Country', GRAPH_LIMITS.country, true);

  const postal = a.postalCode.trim();
  if (!postal) errors.postalCode = 'Postal code is required when entering an address.';
  else {
    if (postal.length > GRAPH_LIMITS.postalCode)
      errors.postalCode = `Postal code must be ${GRAPH_LIMITS.postalCode} characters or fewer.`;
    else if (!POSTAL_RE.test(postal))
      errors.postalCode = 'Postal code may only contain letters, digits, spaces, and hyphens.';
  }

  return errors;
}

// ──────────────────────────────────────────────────────────────────────────────
// Live input formatting + option lists (shared by Build 1 admin editor and Build 2
// self-service). Pure, dependency-free.
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Progressively format a US phone number as `(xxx) xxx-xxxx` while typing.
 * - A leading `+` (international entry) is left untouched — the US mask is not imposed.
 * - A leading country-code `1` on an 11-digit entry (e.g. a pasted `1 555 …`) is dropped.
 * - Digits beyond 10 are ignored. Non-digits are stripped before re-masking.
 * Output stays within `validatePhone` (allowed chars + 7–15 digits).
 */
export function formatUsPhone(input: string): string {
  if (!input) return '';
  if (input.replace(/^\s+/, '').startsWith('+')) return input; // international — leave as typed
  let digits = input.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  digits = digits.slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** US states, DC, and the inhabited territories — `[code, label]`. `code` is what we store. */
export const US_STATES: ReadonlyArray<readonly [string, string]> = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
  ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
  ['DC', 'District of Columbia'], ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'],
  ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'],
  ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'],
  ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'],
  ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'],
  ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'],
  ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'],
  ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'],
  ['SC', 'South Carolina'], ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'],
  ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'],
  ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
  ['AS', 'American Samoa'], ['GU', 'Guam'], ['MP', 'Northern Mariana Islands'],
  ['PR', 'Puerto Rico'], ['VI', 'U.S. Virgin Islands'],
];

const STATE_CODE_SET = new Set(US_STATES.map(([code]) => code));
const STATE_CODE_BY_NAME = new Map(US_STATES.map(([code, label]) => [label.toLowerCase(), code]));

/**
 * Resolve a stored region to its canonical 2-letter code so the dropdown selects it and it
 * round-trips cleanly (we store the CODE; Entra `state` gets the code). Accepts a code in any
 * case, or a full state name. Unknown input (e.g. a non-US region) is returned unchanged.
 */
export function normalizeStateCode(input: string): string {
  const s = (input ?? '').trim();
  if (!s) return '';
  if (STATE_CODE_SET.has(s.toUpperCase())) return s.toUpperCase();
  return STATE_CODE_BY_NAME.get(s.toLowerCase()) ?? s;
}

/** Number of days in a given month. `month` is 1–12. Falls back to a leap-safe 29 for Feb when year is unknown. */
export function daysInMonth(year: number | null, month: number): number {
  if (!month) return 31;
  const y = year && year > 0 ? year : 2000; // 2000 is a leap year → Feb shows 29 before a year is picked
  return new Date(y, month, 0).getDate();
}

/** Month options `[number-1to12-as-string, label]` for a date-of-birth picker. */
export const MONTHS: ReadonlyArray<readonly [string, string]> = [
  ['1', 'January'], ['2', 'February'], ['3', 'March'], ['4', 'April'],
  ['5', 'May'], ['6', 'June'], ['7', 'July'], ['8', 'August'],
  ['9', 'September'], ['10', 'October'], ['11', 'November'], ['12', 'December'],
];
